/**
 * Module-scoped signal channel between the LL/RT player hooks (producers) and
 * the video-QoE controller (the single consumer).
 *
 * The player hooks call `emitVideoQoeSignal(...)` at their internal decision
 * points (failover reason, retry accounting, error classification). When no
 * subscriber is registered — pandatv-mobile, the ASP player, or an adult
 * stream on PC where QoE is disabled — every emit is a no-op set iteration, so
 * producers pay nothing and never throw.
 */

import type { VideoQoeSignal } from './types';

type Listener = (signal: VideoQoeSignal) => void;

const listeners = new Set<Listener>();

/** Publish a signal to the active subscriber, if any. Never throws. */
export const emitVideoQoeSignal = (signal: VideoQoeSignal): void => {
  for (const listener of listeners) {
    try {
      listener(signal);
    } catch {
      // A misbehaving subscriber must never break a player-hook code path.
    }
  }
};

/** Subscribe to signals; returns an unsubscribe function. */
export const subscribeVideoQoeSignals = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
